/**
 * Revenue / Invoicing hooks (REV-040 → REV-049).
 *
 * Wraps the money-side HTTP surface:
 *   - POST /api/v1/invoices:generate       → generate a draft invoice for a case
 *   - POST /api/v1/invoices/{id}:approve   → route a draft into finance review
 *   - POST /api/v1/invoices/{id}:submit    → approve/submit (emits INVOICE_APPROVED)
 *   - POST /api/v1/invoices/{id}/payments  → post a payment
 *   - GET  /api/v1/revenue/ar-aging        → per-hospital AR aging report
 *
 * The Revenue service does not expose a list-invoices endpoint, so this hook
 * tracks the invoices it generates/mutates in session state. Each action
 * returns the server's canonical invoice projection, which we fold back into
 * the tracked list so the table reflects the latest status.
 */
import { useCallback, useState } from 'react';
import { apiClient, ApiError } from '../api/client';
import type { InvoiceLine } from '../api/types';

/**
 * The Revenue service's canonical invoice projection (REV-042+). This is a
 * superset of the shared `Invoice` DTO — Decimal money fields are serialized as
 * JSON numbers, and the wire uses `invoice_lines` for the line array.
 */
export interface RevenueInvoice {
  invoice_id: string;
  tenant_id?: string;
  case_id: string;
  patient_id?: string | null;
  invoice_number?: string | null;
  currency?: string;
  status: 'draft' | 'in_review' | 'approved' | 'submitted' | 'partial' | 'paid' | 'void' | string;
  issue_date?: string | null;
  due_date?: string | null;
  subtotal?: number;
  total: number;
  amount_paid?: number;
  balance_due?: number;
  invoice_lines?: InvoiceLine[];
  submitted_at?: string | null;
}

/** One days-outstanding bucket in the AR aging report (REV-048). */
export interface ArAgingBucket {
  label: string;
  min_days: number;
  max_days: number | null;
  invoice_count: number;
  balance: number;
}

/** Per-hospital AR aging report (REV-048). */
export interface ArAgingReport {
  tenant_id: string;
  as_of: string;
  total_outstanding: number;
  outstanding_invoice_count: number;
  buckets: ArAgingBucket[];
}

interface UseRevenueResult {
  /** Invoices generated/mutated in this session, newest first. */
  invoices: RevenueInvoice[];
  isWorking: boolean;
  error: ApiError | null;
  /** Generate a draft invoice from a case's captured charges. */
  generateInvoice: (caseId: string) => Promise<RevenueInvoice | null>;
  /** Route a draft invoice into finance review. */
  approveInvoice: (invoiceId: string) => Promise<RevenueInvoice | null>;
  /** Approve/submit a reviewed invoice (emits INVOICE_APPROVED). */
  submitInvoice: (invoiceId: string) => Promise<RevenueInvoice | null>;
}

/** Upsert an invoice into a newest-first list, replacing any existing match. */
function upsert(list: RevenueInvoice[], next: RevenueInvoice): RevenueInvoice[] {
  const rest = list.filter((inv) => inv.invoice_id !== next.invoice_id);
  return [next, ...rest];
}

/**
 * Invoice generation + lifecycle actions with a session-tracked invoice list.
 */
export function useRevenue(): UseRevenueResult {
  const [invoices, setInvoices] = useState<RevenueInvoice[]>([]);
  const [isWorking, setIsWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const run = useCallback(
    async (op: () => Promise<RevenueInvoice>): Promise<RevenueInvoice | null> => {
      setIsWorking(true);
      setError(null);
      try {
        const invoice = await op();
        setInvoices((prev) => upsert(prev, invoice));
        return invoice;
      } catch (err: unknown) {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        return null;
      } finally {
        setIsWorking(false);
      }
    },
    []
  );

  const generateInvoice = useCallback(
    (caseId: string) =>
      run(() => apiClient.post<RevenueInvoice>('/invoices:generate', { case_id: caseId })),
    [run]
  );

  const approveInvoice = useCallback(
    (invoiceId: string) =>
      run(() =>
        apiClient.post<RevenueInvoice>(`/invoices/${encodeURIComponent(invoiceId)}:approve`)
      ),
    [run]
  );

  const submitInvoice = useCallback(
    (invoiceId: string) =>
      run(() =>
        apiClient.post<RevenueInvoice>(`/invoices/${encodeURIComponent(invoiceId)}:submit`)
      ),
    [run]
  );

  return { invoices, isWorking, error, generateInvoice, approveInvoice, submitInvoice };
}

interface UseArAgingResult {
  report: ArAgingReport | null;
  isLoading: boolean;
  error: ApiError | null;
  refresh: () => void;
}

/** Loads the per-hospital AR aging report (REV-048) for the active tenant. */
export function useArAging(): UseArAgingResult {
  const [report, setReport] = useState<ArAgingReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const refresh = useCallback(() => {
    setIsLoading(true);
    setError(null);
    apiClient
      .get<ArAgingReport>('/revenue/ar-aging')
      .then((data) => {
        setReport(data);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err : new ApiError(0, 'ERROR', String(err)));
        setReport(null);
      })
      .finally(() => setIsLoading(false));
  }, []);

  return { report, isLoading, error, refresh };
}
